import React, { FunctionComponent, ReactElement, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Link, Modal, Button } from '@ds/ui';
import { inkSpacingTokens } from '@ds/tokens';
import { Interpolate, TranslationContext } from '@ds/react-utils';
import { ConditionalChildren } from 'layouts';

type ResendModalProps = {
    countdown: number;
    resetCountdown: () => void;
    method: string;
    displayChannel: string;
    onNewSms: () => void;
    onNewCall: () => void;
    onNewEmail: () => void;
};

const ResendModalContainer = styled.div`
    margin-top: ${inkSpacingTokens.spacingXS};
`;

const ResendModal: FunctionComponent<ResendModalProps> = ({
    countdown,
    resetCountdown,
    method,
    displayChannel,
    onNewSms,
    onNewCall,
    onNewEmail
}): ReactElement | null => {
    const translate = useContext(TranslationContext);
    const [modalVisible, setModalVisible] = useState(false);

    const onSMSClick = () => {
        setModalVisible(false);
        resetCountdown();
        onNewSms();
    };

    const onCallClick = () => {
        setModalVisible(false);
        resetCountdown();
        onNewCall();
    };

    const onEmailClick = () => {
        setModalVisible(false);
        resetCountdown();
        onNewEmail();
    };

    return (
        <ResendModalContainer>
            <Link discrete={true} text={translate('Resend Code')} data-qa={'verify-resend-modal-link'} onClick={() => setModalVisible(true)} />
            <Modal
                width="small"
                onClose={() => setModalVisible(false)}
                visible={modalVisible}
                closeButton={
                    <div data-qa="modal-close">
                        <Modal.Close onClick={() => setModalVisible(false)} />
                    </div>
                }
            >
                <Modal.Header title={translate('Resend Code')} data-qa={'verify-resend-modal-header'} />

                <Modal.Body>
                    <div data-qa={'verify-resend-modal-content'}>
                        <ConditionalChildren condition={countdown > 0} fallback={<>{translate('Select how to receive your new code')}</>}>
                            <Interpolate
                                text={translate('Please wait a moment: <Countdown></Countdown>')}
                                components={{
                                    Countdown: () => countdown.toString()
                                }}
                            />
                        </ConditionalChildren>
                    </div>
                </Modal.Body>

                <ConditionalChildren condition={method === 'SMS'}>
                    <Modal.Footer
                        primaryButton={
                            <Button
                                size="small"
                                kind="primary"
                                data-qa={'verify-resend-modal-primary-button'}
                                text={translate('Text Message')}
                                onClick={onSMSClick}
                                disabled={countdown !== 0}
                            />
                        }
                        secondaryButton={
                            displayChannel === 'SMSandPhoneCall' ? <Button
                                size="small"
                                kind="tertiary"
                                data-qa={'verify-resend-modal-secondary-button'}
                                text={translate('Phone Call')}
                                onClick={onCallClick}
                                disabled={countdown !== 0}
                            /> : undefined
                        }
                    />
                </ConditionalChildren>
                <ConditionalChildren condition={method === 'PhoneCall'}>
                    <Modal.Footer
                        primaryButton={
                            <Button
                                size="small"
                                kind="primary"
                                data-qa={'verify-resend-modal-primary-button'}
                                text={translate('Phone Call')}
                                onClick={onCallClick}
                                disabled={countdown !== 0}
                            />
                        }
                        secondaryButton={
                             displayChannel === 'SMSandPhoneCall' ? <Button
                                size="small"
                                kind="tertiary"
                                data-qa={'verify-resend-modal-secondary-button'}
                                text={translate('Text Message')}
                                onClick={onSMSClick}
                                disabled={countdown !== 0}
                            /> : undefined
                        }
                    />
                </ConditionalChildren>
                <ConditionalChildren condition={method === 'Email'}>
                    <Modal.Footer
                        primaryButton={
                            <Button
                                size="small"
                                kind="primary"
                                data-qa={'verify-resend-modal-primary-button'}
                                text={translate('Resend Code')}
                                onClick={onEmailClick}
                                disabled={countdown !== 0}
                            />
                        }
                    />
                </ConditionalChildren>
            </Modal>
        </ResendModalContainer>
    );
};

export default ResendModal;
